import { CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DeclinePriceNegotiationDialogProps {
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
}

export function DeclinePriceNegotiationDialog({
  onClose,
  onConfirm,
  isLoading = false,
}: Readonly<DeclinePriceNegotiationDialogProps>) {
  const { t } = useTranslation('order');
  const [reason, setReason] = useState('');

  return (
    <div
      data-testid="decline-price-negotiation-dialog"
      className="bg-neut-bg-alt rounded-lg max-w-md w-full p-6"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-warning-100 rounded-lg">
          <CircleAlert
            className="text-warning-600"
            size={24}
            aria-hidden="true"
          />
        </div>
        <h3 className="text-xl text-neut-prim font-medium">
          {t('order.decline_price_negotiation_title')}
        </h3>
      </div>

      <p className="text-neut-sec mb-4 font-light text-sm">
        {t('order.decline_price_negotiation_body')}
      </p>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="decline-reason"
            className="block text-sm font-normal text-neut-700 mb-1"
          >
            {t('order.decline_price_negotiation_reason_label')}
          </label>
          <textarea
            id="decline-reason"
            rows={3}
            disabled={isLoading}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t(
              'order.decline_price_negotiation_reason_placeholder'
            )}
            className="w-full px-3 py-2 border border-neut-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-warning-500 font-normal"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-neut-300 rounded-lg hover:bg-neut-50 transition-colors font-normal disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('order.decline_price_negotiation_cancel')}
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-warning-500 text-neut-0 rounded-lg hover:bg-warning-600 transition-colors font-normal whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('order.decline_price_negotiation_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
